import { css, SerializedStyles } from '@emotion/core';
import styled from '@emotion/styled';
import { Tab as MuiTab, Tabs as MuiTabs } from '@material-ui/core';

import { ICustomTheme } from 'interfaces';
import { CustomConnector as StepperCustomConnector } from 'components/Stepper/styles';

type Theme = {
  theme?: ICustomTheme;
};

export const cssBannerContainer = (theme: ICustomTheme): SerializedStyles =>
  css({
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3, 0),
    },
  });

export const cssCustomStepper = (theme: ICustomTheme): SerializedStyles =>
  css({
    '& .MuiStep-completed': {
      '&:last-child': {
        '& .MuiStepLabel-root': {
          position: 'relative',
          // the only way I found to have the line when last step is completed
          '&::after': {
            backgroundColor: theme.colors.paletteFromBO.main,
            bottom: 15,
            content: '""',
            display: 'block',
            height: 80,
            position: 'absolute',
            width: 4,
            zIndex: -1,
          },
        },
      },
      '& .MuiStepConnector-line': {
        background: theme.colors.paletteFromBO.main,
      },
    },
  });

export const cssMarginTop = (theme: ICustomTheme): SerializedStyles =>
  css({
    marginTop: theme.spacing(2),
  });

export const cssPercentBlock = (theme: ICustomTheme): SerializedStyles =>
  css({
    alignItems: 'center',
    backgroundColor: theme.palette.grey[200],
    display: 'flex',
    padding: theme.spacing(1, 4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 2),
    },
  });

export const cssStepperContainer = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  textAlign: 'center',
});

export const Section = styled.div<Theme>({
  width: '100%',
  minHeight: 'inherit',
});

export const getCssResultStep =
  (index: number) =>
  (theme: ICustomTheme): SerializedStyles =>
    css({
      counterReset: `step ${index}`,
      /*
       * "counterReset: `step ${index}`" is a css counter
       * which represents the step number and is used in CustomConnector
       */
      [theme.breakpoints.down('xs')]: {
        minWidth: 200,
      },
    });

export const Container = styled.div(() => ({
  flex: '1 0 auto',
}));

export const CustomConnector = styled(StepperCustomConnector)<{
  disabled: boolean;
  theme: ICustomTheme;
  value?: number;
  numberOfStep: number;
}>(({ disabled, theme, value = 0, numberOfStep }) => ({
  /* the only way I found to add the step under the connector */
  ...(numberOfStep > 2 && {
    '&::after': {
      bottom: -25,
      color: theme.palette.text.primary,
      /*
       * "counter(step)" represents the step number, related to
       * "counterReset: `step ${index}`" in getCssResultStep
       */
      display: 'block',
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.fontWeightBold,
      left: 0,
      position: 'absolute',
      textAlign: 'center',
      width: '100%',
    },
  }),
  ...(!disabled && {
    '&.MuiStepConnector-active': {
      '& .MuiStepConnector-line': {
        '&::before': {
          backgroundColor: theme.colors.paletteFromBO.main,
          backgroundSize: '16px 16px',
          content: '""',
          display: 'block',
          height: '100%',
          transform: `translateX(-${100 - value}%)`,
          width: '100%',
        },
        '&::after': {
          backgroundColor: theme.colors.paletteFromBO.main,
          bottom: 0,
          content: '""',
          display: 'block',
          height: 80,
          position: 'absolute',
          /* the calculation below moves the connector's line according to the result (value) */
          left:
            value === 0
              ? -22
              : `calc(${
                  (theme.spacing(1.5) * (100 - 2 * value)) / 100 - 2
                }px + ${value}%)`,
          width: 4,
        },
      },
    },
  }),
}));

export const Tab = styled(MuiTab)<Theme>(({ theme }) => ({
  marginRight: theme.spacing(3),
  minHeight: 'unset',
  minWidth: 'unset',
  padding: 0,
  textTransform: 'initial',
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.h5.fontSize,
  },
}));

export const Tabs = styled(MuiTabs)<Theme>(({ theme }) => ({
  marginBottom: theme.spacing(3),
  minHeight: 'unset',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  },
}));
